import { makeAutoObservable } from "mobx";

class DisclosureStore {
  selectedFrameworks = [];
  frameworks = [];
  activeCategory = "Environmental";
  disclosures = [];
  expandedDisclosures = new Set();
  activeDisclosure = null;
  searchDisclosure = "";
  reportingBoundary = null;
  disclosurePresence = new Set();
  presenceFilter = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * All disclosures matching the active framework chips + search query,
   * but NOT yet narrowed by category. Used by CategoryTabs so each tab's
   * count reflects what would land in that category under the current
   * search/framework state.
   */
  get disclosuresMatchingNonCategoryFilters() {
    let result = this.disclosures;
    if (this.selectedFrameworks.length > 0) {
      const selected = new Set(this.selectedFrameworks);
      result = result.filter((d) => selected.has(d.framework));
    }
    const q = this.searchDisclosure?.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (d) =>
          d.code?.toLowerCase().includes(q) ||
          d.title?.toLowerCase().includes(q),
      );
    }
    return result;
  }

  get filteredDisclosures() {
    let result;
    if (this.activeCategory === "All Metrics") {
      result = this.disclosuresMatchingNonCategoryFilters;
    } else {
      result = this.disclosuresMatchingNonCategoryFilters.filter(
        (d) => d.category === this.activeCategory,
      );
    }
    if (this.presenceFilter === "filled") {
      return result.filter((d) => this.disclosurePresence.has(d._id));
    }
    if (this.presenceFilter === "empty") {
      return result.filter((d) => !this.disclosurePresence.has(d._id));
    }
    return result;
  }

  setFrameworks = (frameworks) => {
    this.frameworks = Array.isArray(frameworks) ? frameworks : [];

    const availableFrameworkCodes = new Set(
      this.frameworks.map((framework) => framework.code).filter(Boolean),
    );

    this.selectedFrameworks = this.selectedFrameworks.filter((framework) =>
      availableFrameworkCodes.has(framework),
    );

    if (
      this.activeDisclosure &&
      !availableFrameworkCodes.has(this.activeDisclosure.framework)
    ) {
      this.activeDisclosure = null;
    }
  };

  setSelectedFrameworks = (frameworks) => {
    this.selectedFrameworks = frameworks;
    this.activeDisclosure = null;
  };

  setActiveCategory = (category) => {
    this.activeCategory = category;
    this.presenceFilter = null;
  };

  setPresenceFilter = (filter) => {
    this.presenceFilter = this.presenceFilter === filter ? null : filter;
  };

  setDisclosures = (disclosures) => {
    this.disclosures = disclosures;
    if (this.activeDisclosure) {
      const refreshed = disclosures.find(
        (d) => d._id === this.activeDisclosure._id,
      );
      if (refreshed) this.activeDisclosure = refreshed;
    }
  };

  toggleExpandDisclosure = (code) => {
    if (this.expandedDisclosures.has(code)) {
      this.expandedDisclosures.delete(code);
    } else {
      this.expandedDisclosures.add(code);
    }
  };

  setActiveDisclosure = (disclosure) => (this.activeDisclosure = disclosure);

  setSearchDisclosure = (search) => (this.searchDisclosure = search);

  setReportingBoundary = (boundary) => (this.reportingBoundary = boundary);

  setDisclosurePresence = (ids) => {
    this.disclosurePresence = new Set(ids);
  };

  reset = () => {
    this.selectedFrameworks = [];
    this.frameworks = [];
    this.activeCategory = "Environmental";
    this.disclosures = [];
    this.expandedDisclosures = new Set();
    this.activeDisclosure = null;
    this.searchDisclosure = "";
    this.reportingBoundary = null;
    this.disclosurePresence = new Set();
    this.presenceFilter = null;
  };
}

export const disclosureStore = new DisclosureStore();
